import fp from 'lodash/fp';
import React, { useEffect, ComponentType } from 'react';

import { Badge, usePrevious } from 'Modules/ui';
import { useAction } from 'Modules/core/redux';

import { paginationActions } from '../../state/pagination/actions';
import { paginationStatuses } from '../../state/pagination/statuses';
import { IPaginationParameters } from '../../state/types';
import { usePagination } from '../../utils';


type PaginationCountBadgeType = {
    path: string;
    parameters: IPaginationParameters;
    refreshOnDirty?: boolean;
}

export const PaginationCountBadge: ComponentType<PaginationCountBadgeType> = ({
    path,
    parameters,
    refreshOnDirty,
}) => {
    const pagination = usePagination(path);

    const setParameters = useAction<void, IPaginationParameters>(
        () => paginationActions.setParameters(path, { ...parameters }),
        [path, parameters],
    );
    const requestTotal = useAction(
        () => paginationActions.request(path, { start: 0, stop: 0 }),
        [path],
    );

    const prevDirty = usePrevious(fp.get('dirty', pagination));
    useEffect(
        () => {
            if (!path)
                return;

            if (!pagination) {
                setParameters();
                return;
            }

            const { total, queue, dirty } = pagination;
            const shouldRequestTotal = (!fp.isNumber(total) ||
                refreshOnDirty && prevDirty !== dirty) &&
                queue['0:0'] !== paginationStatuses.PENDING;

            if (shouldRequestTotal)
                requestTotal();
        },
        [pagination, prevDirty, parameters, path, refreshOnDirty, setParameters, requestTotal],
    );

    if (!pagination || !pagination.total || pagination.total === 0)
        return null;

    return <Badge count={pagination.total} />;
};
